import React, { useEffect, useState } from 'react';
import {
  Box,
  ScrollArea,
  Text,
  Modal,
  TextInput,
  Textarea,
  Button
} from '@mantine/core';
import { IconThumbUp, IconThumbDown } from '@tabler/icons-react';
import { useChatDispatch, useChatState } from '../context';
import { ChatTypingAnimation } from './chat-typing-animation';
import { useDarkMode } from 'usehooks-ts';
import {
  createFeedback,
  updateConversation
} from '../../../../graphql/mutations';
import { v4 as uuidv4 } from 'uuid';
import { Amplify } from 'aws-amplify';
import { onCreateMessage } from '../../../../graphql/subscriptions';
import {
  getConversation,
  listFeedback,
  listMessages
} from '../../../../graphql/queries';
import { generateClient } from 'aws-amplify/api';
import { CitationReferenceList } from './citation';

const json5 = require('json5');

export const ChatContent = () => {
  const [messages, setMessages] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [messageId, setMessageId] = useState('');
  const [feedbackState, setFeedbackState] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [title, setTitle] = useState('');
  const dispatch = useChatDispatch();

  const [description, setDescription] = useState('');
  const { selectedChat, userId } = useChatState();
  function capitalizeFirstLetter(string) {
    return string?.charAt(0)?.toUpperCase() + string?.slice(1);
  }

  const capitalizedUserId = capitalizeFirstLetter(userId);
  const [loading, setLoading] = useState(false);

  const client = generateClient();

  Amplify.configure({
    API: {
      GraphQL: {
        endpoint: process.env.REACT_APP_API_URI,
        region: process.env.REACT_APP_AWS_REGION,
        defaultAuthMode: 'apiKey',
        apiKey: process.env.REACT_APP_APPSYNC_API_KEY
      }
    }
  });

  const getAllListMessages = async () => {
    setLoading(true);

    const variables = {
      filter: {
        conversationId: { eq: selectedChat?.id }
      }
    };

    const data = await client.graphql({
      query: listMessages,
      variables
    });

    if (data?.data) {
      setLoading(false);
    }
    setMessages(data?.data?.listMessages?.items);
  };

  const getAllListFeedback = async () => {
    setLoading(true);

    const variables = {
      filter: {
        conversationId: { eq: selectedChat?.id }
      }
    };

    const data = await client.graphql({
      query: listFeedback,
      variables
    });

    if (data?.data) {
      setLoading(false);
    }
    console.log(data?.data?.listFeedback?.items);
    setFeedback(data?.data?.listFeedback?.items);
  };

  useEffect(() => {
    if (selectedChat?.id) {
      setNewMessageId(null);
      getAllListMessages();
      getAllListFeedback();
    }
  }, [selectedChat]);

  const [animatedMessages, setAnimatedMessages] = useState({});

  const [newMessageId, setNewMessageId] = useState(null);
  useEffect(() => {
    if (messages.length > 0) {
      const newMessages = messages.filter((msg) =>
        newMessageId?.includes(msg.id)
      );

      newMessages.forEach((lastMessage) => {
        if (!lastMessage.isBot || animatedMessages[lastMessage.id]) return;

        const fullText = JSON.parse(lastMessage.content)?.response || '';
        let currentText = '';
        let index = 0;

        const interval = setInterval(() => {
          currentText += fullText[index];
          setAnimatedMessages((prev) => ({
            ...prev,
            [lastMessage.id]: currentText
          }));

          index++;
          if (index === fullText.length) clearInterval(interval);
        }, 50);

        return () => clearInterval(interval);
      });
    }
  }, [messages]);

  const handleFeedback = async () => {
    setFeedbackLoading(true);
    setFeedbackError(null);

    const feedbackId = uuidv4();
    const input = {
      modelId: 'claude',
      messageId,
      conversationId: selectedChat?.id,
      userId: capitalizedUserId,
      type: 'sentiment',
      content: JSON.stringify({
        liked: feedbackType === 'liked' ? true : false,
        comment: { title, description }
      })
    };
    try {
      const data = await client.graphql({
        query: createFeedback,
        variables: {
          input
        }
      });
      setFeedbackState(feedbackType);
      feedback.push(data?.data?.createFeedback);
      setModalOpen(false);
      setTitle('');
      setDescription('');

      return { success: true, feedbackId };
    } catch (error) {
      console.error('Error saving feedback:', error);
      setFeedbackError('Failed to submit feedback. Please try again.');
      return { success: false, error };
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleSubmit = async () => {
    const result = await handleFeedback();
    if (result.success) {
      console.log('Feedback Submitted:', { feedbackType, title, description });
    }
  };

  useEffect(() => {
    const createSub = client
      .graphql({
        query: onCreateMessage,
        variables: { conversationId: selectedChat?.id }
      })
      .subscribe({
        next: async ({ data }) => {
          setMessages((prevMessages) => {
            const exists = prevMessages.some(
              (msg) => msg.id === data.onCreateMessage.id
            );
            if (exists) return prevMessages;
            return [...prevMessages, data.onCreateMessage];
          });

          if (data?.onCreateMessage?.isBot) {
            const conversationId = data?.onCreateMessage?.conversationId;
            if (!conversationId) {
              console.warn('No conversationId found in the message');
              return;
            }

            try {
              const conversationData = await client.graphql({
                query: getConversation,
                variables: { id: conversationId }
              });

              dispatch({
                type: 'SET_SELECTED_CHAT',
                payload: conversationData?.data?.getConversation
              });
              setNewMessageId(data.onCreateMessage.id);
            } catch (error) {
              console.error('Error fetching conversation:', error);
            }
          }
        },
        error: (error) => console.warn(error)
      });

    return () => createSub.unsubscribe();
  }, []);

  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    if (!selectedChat || !selectedChat.id || messages.length === 0) return;
    const sortedConversations = messages.sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    );
    const firstMessage = JSON.parse(
      sortedConversations[0]?.content || '{}'
    )?.query;
    const secondMessage =
      JSON.parse(sortedConversations[3]?.content || '{}')?.query || '';

    const newTitle = firstMessage == 'Hello' ? secondMessage : null;
    if (
      !newTitle ||
      !(selectedChat?.title && selectedChat.title.startsWith('Conversation'))
    )
      return;

    const updateConversationTitle = async () => {
      try {
        const data = await client.graphql({
          query: updateConversation,
          variables: { input: { id: selectedChat.id, title: newTitle } }
        });

        console.log('Updated Conversation:', data);
      } catch (error) {
        console.error('Error updating conversation:', error);
      }
    };

    updateConversationTitle();
  }, [selectedChat, messages]);

  return (
    <>
      <ScrollArea
        style={{
          height: '100%',
          backgroundColor: !isDarkMode ? '#FFFFFF' : '#FFFFFF',
          color: !isDarkMode ? '#FFFFFF' : '#000000'
        }}
      >
        {loading ? (
          <ChatTypingAnimation input={'Loading'} />
        ) : messages?.length > 0 && selectedChat?.id !== undefined ? (
          <div>
            {messages?.length > 0 && selectedChat?.id !== undefined ? (
              (() => {
                const formatDate = (dateString) => {
                  const options = {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  };
                  const date = new Date(dateString);
                  const today = new Date();
                  return date.toDateString() === today.toDateString()
                    ? 'Today'
                    : date.toLocaleDateString([], options);
                };

                const sortedMessages = [...messages].sort((a, b) => {
                  const dateA = new Date(a.timestamp).getTime();
                  const dateB = new Date(b.timestamp).getTime();

                  if (dateA === dateB) {
                    return a.isBot ? 1 : -1;
                  }

                  return dateA - dateB;
                });

                const groupedMessages = sortedMessages?.reduce(
                  (acc, message) => {
                    const date = new Date(message.timestamp).toDateString();
                    acc[date] = acc[date] || [];
                    acc[date].push(message);
                    return acc;
                  },
                  {}
                );

                return Object.entries(groupedMessages).map(([date, chat]) => (
                  <div key={date} className="chat-container">
                    <div className="chat-date">{formatDate(date)}</div>
                    {chat.map((message) => {
                      const isSender = !message.isBot;
                      const formatTime = (dateString) => {
                        const date = new Date(dateString);
                        return date.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        });
                      };

                      let references = [];
                      if (message.isBot && message.citations) {
                        const regex =
                          /retrievedReferences':\s*(\[[\s\S]*?\])\}/;
                        const match = message.citations.match(regex);

                        if (match && match[1]) {
                          try {
                            const cleanedContent = match[1]
                              .replace(/\\{2}/g, '\\')
                              .replace(/\\"/g, '"');

                            references = json5.parse(cleanedContent);
                          } catch (error) {
                            console.error(
                              'Failed to parse JSON5 content:',
                              error
                            );
                          }
                        }
                      }
                      const hasFeedback =
                        feedback?.some(
                          (item) => item.messageId === message.id
                        ) ?? false;
                      const messageStatus = () => {
                        const feedbackData = feedback?.find(
                          (item) => item.messageId === message.id
                        );
                        if (!feedbackData) return null;

                        try {
                          const content = JSON.parse(feedbackData.content);
                          return content?.liked ?? false;
                        } catch {
                          return false;
                        }
                      };
                      const isMessageLiked = messageStatus();
                      const response = JSON.parse(message.content)?.response;
                      return (
                        <div
                          key={message.id}
                          className={`chat-message ${
                            isSender ? 'chat-right' : 'chat-left'
                          }`}
                        >
                          {!Array.isArray(response) ? (
                            <div className="chat-content">
                              <p className="chat-time">
                                {formatTime(message.timestamp)}
                              </p>
                              <>
                                <div className="chat-bubble">
                                  <p>
                                    {newMessageId === message.id
                                      ? animatedMessages[message.id] || ''
                                      : JSON.parse(message.content)?.query ||
                                        JSON.parse(message.content)?.response}
                                  </p>
                                </div>
                                {message.isBot && (
                                  <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                                    {!hasFeedback && (
                                      <div
                                        style={{
                                          display: 'flex',
                                          gap: 4,
                                          marginBottom: '4px',
                                          marginTop: '4px'
                                        }}
                                      >
                                        <IconThumbUp
                                          size={24}
                                          onClick={() => {
                                            setFeedbackType('liked');
                                            setMessageId(message.id);
                                            setModalOpen(true);
                                          }}
                                          style={{ cursor: 'pointer' }}
                                        />
                                        <IconThumbDown
                                          size={24}
                                          onClick={() => {
                                            setFeedbackType('disliked');
                                            setMessageId(message.id);
                                            setModalOpen(true);
                                          }}
                                          style={{ cursor: 'pointer' }}
                                        />
                                      </div>
                                    )}
                                    {isMessageLiked && hasFeedback && (
                                      <div
                                        style={{
                                          display: 'flex',
                                          gap: 4,
                                          marginBottom: '4px',
                                          marginTop: '4px'
                                        }}
                                      >
                                        <IconThumbUp size={24} color="blue" />
                                      </div>
                                    )}
                                    {!isMessageLiked && hasFeedback && (
                                      <div
                                        style={{
                                          display: 'flex',
                                          gap: 4,
                                          marginBottom: '4px',
                                          marginTop: '4px'
                                        }}
                                      >
                                        <IconThumbDown size={24} color="red" />
                                      </div>
                                    )}
                                  </Box>
                                )}
                                {message.isBot && (
                                  <Box
                                    sx={{
                                      marginBottom: 2,
                                      maxWidth: '400px',
                                      width: '100%'
                                    }}
                                  >
                                    <CitationReferenceList
                                      references={references}
                                    />
                                  </Box>
                                )}
                              </>
                            </div>
                          ) : (
                            <div>
                              <p>{response?.[0]}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ));
              })()
            ) : (
              <p className="no-messages">No messages to display</p>
            )}
          </div>
        ) : (
          <Text>No messages yet!</Text>
        )}
        <Modal
          opened={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Submit Feedback"
          centered
        >
          <TextInput
            label="Title"
            placeholder="Enter a title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Textarea
            label="Description"
            placeholder="Enter your feedback"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            mt="sm"
          />
          <Button fullWidth mt="md" onClick={handleSubmit}>
            Submit
          </Button>
        </Modal>
      </ScrollArea>
    </>
  );
};
